<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		Select,
		Slider,
		Toggle,
		ColorWheel,
		Text,
		Alert,
		type SelectOptionPropsType,
	} from '@Shelly/shelly-design-system';
	import { capFirstChar } from '../../../../../../../../../helpers/utilities';
	import {
		generateCuryActionOptions,
		generateCurySlotOptions,
		createCuryActionMappings,
		loadCuryUIState,
		generateCuryActionPreview,
	} from '../../../helpers/cury-diffuser-helpers';

	export const actions = [];
	export const device = undefined;
	export const sceneIsValid = false;
	export let active_actions = [];

	const dispatch = createEventDispatcher();

	const actionOptions: SelectOptionPropsType[] = generateCuryActionOptions();
	const slotOptions: SelectOptionPropsType[] = generateCurySlotOptions();

	// Initialize UI state
	let selectedAction: SelectOptionPropsType = actionOptions[0];
	let intensity = 50;
	let slot = 'left';
	let selectedSlot: SelectOptionPropsType = slotOptions[0];
	let ambientEnabled = false;
	let ambientBrightness = 50;
	let ambientColor = [255, 0, 0];
	let loaded = false;

	// Load from active_actions ONCE when data arrives
	$: if (active_actions.length > 0 && !loaded) {
		const state = loadCuryUIState(active_actions, actionOptions, slotOptions);
		selectedAction = state.selectedAction;
		intensity = state.intensity;
		slot = state.slot;
		selectedSlot = state.selectedSlot;
		ambientEnabled = state.ambientEnabled;
		ambientBrightness = state.ambientBrightness;
		ambientColor = state.ambientColor;
		loaded = true;
	}

	const updateActions = () => {
		if (!selectedAction?.value || selectedAction.value === 0) {
			// No action selected or placeholder selected - clear actions
			dispatch('change_active', { new_active: [] });
			return;
		}
		const mappings = createCuryActionMappings(
			intensity,
			slot,
			ambientEnabled,
			ambientBrightness,
			ambientColor,
		);
		const actionCreator = mappings[selectedAction.value as string];
		if (actionCreator) {
			dispatch('change_active', { new_active: actionCreator() });
		}
	};

	$: actionPreview = generateCuryActionPreview(
		selectedAction || actionOptions[0],
		slot,
		intensity,
		ambientEnabled || false,
		ambientBrightness || 50,
	);

	const intensityA11yProps = {
		ariaLabel: $format('label_intensity', { default: 'Intensity' }),
		ariaLabelValue: $format('label_intensity', { default: 'Intensity' }),
	}; //we need to use variable to prevent lint errors

	const brightnessA11yProps = {
		ariaLabel: $format('label_brightness', { default: 'Brightness' }),
		ariaLabelValue: $format('label_brightness', { default: 'Brightness' }),
	}; //here also
</script>

<Text element="h2" class="mb-s">
	{capFirstChar($format('collapse_title_change_state', { default: 'Change state' }))}
</Text>

<div class="mb-s">
	<Text element="h4" class="mb-xs"
		>{$format('title_select_cury_action', {
			default: 'Select Cury Action',
		})}</Text
	>
	<Select
		id="select-cury-action"
		aria-label={$format('title_select_cury_action', {
			default: 'Select Cury Action',
		})}
		size="m"
		value={selectedAction}
		icon={null}
		options={actionOptions}
		on:change={(e) => {
			if (typeof e.detail === 'object') {
				selectedAction = e.detail;
				updateActions();
			}
		}}
	/>
</div>

{#if selectedAction.value === 'cury_set'}
	<div class="mb-s">
		<Text element="h4" class="mb-xs">{$format('select_slot', { default: 'Select Slot' })}</Text>
		<Select
			id="select-slot"
			aria-label={$format('select_slot', { default: 'Select Slot' })}
			size="m"
			value={selectedSlot}
			icon={null}
			options={slotOptions}
			on:change={(e) => {
				if (typeof e.detail === 'object') {
					selectedSlot = e.detail;
					slot = String(selectedSlot.value);
					updateActions();
				}
			}}
		/>
	</div>
	<div class="mb-s">
		<Text element="h4" class="mb-xs"
			>{$format('label_set_intensity', { default: 'Set Intensity' })}</Text
		>
		<Slider
			id="slider-intensity"
			a11yProps={intensityA11yProps}
			type="simple"
			value={intensity}
			title={$format('label_intensity', { default: 'Intensity' })}
			min={0}
			max={100}
			unit="%"
			on:value_change={(e) => {
				intensity = e.detail.value;
				updateActions();
			}}
		/>
	</div>
{/if}

{#if selectedAction.value === 'cury_setconfig'}
	<div class="mb-s">
		<Text element="h4" class="mb-xs"
			>{$format('ambient_light_settings', {
				default: 'Ambient Light Settings',
			})}</Text
		>
		<div class="mb-s">
			<Toggle
				id="toggle-ambient-light"
				bind:checked={ambientEnabled}
				on:click={() => {
					setTimeout(() => updateActions(), 0);
				}}
			>
				{$format('label_enable', { default: 'Enable' })}
			</Toggle>
		</div>
		{#if ambientEnabled}
			<div class="mb-s">
				<Text element="h5" class="mb-xs"
					>{$format('label_brightness', { default: 'Brightness' })}</Text
				>
				<Slider
					id="slider-brightness"
					a11yProps={brightnessA11yProps}
					type="simple"
					value={ambientBrightness}
					title={$format('label_brightness', { default: 'Brightness' })}
					min={0}
					max={100}
					unit="%"
					on:value_change={(e) => {
						ambientBrightness = e.detail.value;
						updateActions();
					}}
				/>
			</div>
			<div class="mb-s">
				<Text element="h5" class="mb-xs"
					>{capFirstChar($format('label_color', { default: 'Color' }))}</Text
				>
				<div class="d-flex justify-content-center">
					<div style="max-width: 300px;">
						<ColorWheel
							id="color-wheel"
							color={{
								r: ambientColor?.[0] ?? 255,
								g: ambientColor?.[1] ?? 255,
								b: ambientColor?.[2] ?? 255,
							}}
							hideSaveBtn
							on:color_change={(e) => {
								const c = e.detail.color;
								ambientColor = [c.r, c.g, c.b];
								updateActions();
							}}
						/>
					</div>
				</div>
			</div>
		{/if}
	</div>
{/if}

{#if actionPreview}
	<Alert type="info" heading={actionPreview} />
{/if}
